import { ProcessLayoutMode } from "@certa/studio-core";
import type { EventHandlerCallback } from "../engines/coreComponents/types";
import { DragType } from "../engines/interactionEngine/interactionObserverConstants";
import type { NodeStyle } from "../engines/layoutEngine/layout.types";
import type { FSC, SCNode } from "../engines/renderingEngine/fsc.types";
import {
  BoxNode,
  IconNode,
  TextNode
} from "../engines/renderingEngine/primitiveNodes";
import { HorizontalDropIndicator } from "./HorizontalDropIndicator";
import { VERTICAL_DROP_INDICATOR_WIDTH } from "./VerticalDropIndicator";
import { PALETTE, THEME } from "./colorPalette.constants";
import { styles } from "./scomponents.styles";
import { getSwimlaneMaxLength } from "./scomponents.utils";

type SwimlaneNodeProps = {
  title: string;
  selected: boolean;
  children: SCNode<unknown>[];
  addChildNode?: SCNode<unknown>;
  key: string;
  expanded?: boolean;
  onCollapseToggle: EventHandlerCallback;
  onClick: EventHandlerCallback;
  onDropped: EventHandlerCallback;
  onDragStart: EventHandlerCallback;
  onDropping?: EventHandlerCallback;
  onDroppingOut?: EventHandlerCallback;

  theme: THEME;
  styleOverride: Partial<NodeStyle>;
  readonly: boolean;
  upStreamRulesCount: number;
  downStreamRulesCount: number;
  onUpStreamRuleIndicatorClick: () => void;
  onDownStreamRuleIndicatorClick: () => void;
  disableExpandAndCollapse?: boolean;

  isDroppingBefore?: boolean;
  dropIndicatorIndex: number;
  layoutMode?: ProcessLayoutMode;
};

const DROP_TYPES_HEADER = [
  DragType.STEP,
  DragType.FIELD,
  DragType.NEW_FIELD,
  DragType.TEMPLATE_STEP
];

const DROP_TYPES_CONTAINER = [DragType.SWIMLANE];

export const SwimlaneNode: FSC<SwimlaneNodeProps> = ({
  key,
  selected,
  children,
  addChildNode,
  expanded = true,
  onCollapseToggle,
  onClick,
  onDropped,
  onDragStart,
  readonly,
  onDropping,
  onDroppingOut,
  theme = THEME.LIGHT,
  styleOverride,
  title,
  upStreamRulesCount,
  downStreamRulesCount,
  onDownStreamRuleIndicatorClick,
  onUpStreamRuleIndicatorClick,
  isDroppingBefore,
  dropIndicatorIndex,
  disableExpandAndCollapse,
  layoutMode = ProcessLayoutMode.STRUCTURE_VIEW
}) => {
  const style = styles[theme].swimlane;
  const palette = PALETTE[theme];
  const isFlowView = layoutMode === ProcessLayoutMode.FLOW_VIEW;
  /**
   * These are props only applicable when we're NOT in readonly mode.
   */
  const nonReadonlyPropsHeader: Partial<Parameters<typeof BoxNode>[0]> =
    readonly
      ? {}
      : {
          dragType: DragType.SWIMLANE,
          dropTypes: DROP_TYPES_HEADER,
          dragHandleFor: key,
          eventHandlers: {
            onDropped: onDropped,
            onDragStart: onDragStart,
            onDropping: onDropping,
            onDroppingOut: onDroppingOut
          }
        };

  const nonReadonlyPropsContainer: Partial<Parameters<typeof BoxNode>[0]> =
    readonly
      ? {}
      : {
          // TODO: We have to also add drag type to container mentioned in `dragHandleFor`
          // Because when checking for "dropping", we usually just find the dragType using
          // the node that's in dragContext (at that point we don't know who the "handler" was)
          // Therefore, since we have added this prop to the container as well.
          // This is an intermediate fix until we figure out a better way to do this.
          dragType: DragType.SWIMLANE,
          dropTypes: DROP_TYPES_CONTAINER,
          eventHandlers: {
            onDropped: onDropped,
            onDropping: onDropping,
            onDroppingOut: onDroppingOut
          }
        };

  const upstreamIndicator = BoxNode({
    key: `${key}-SwimlaneUpStreamRuleIndicatorContainer`,
    style: { ...style.ruleIndicatorContainer, marginRight: 5 },
    eventHandlers: {
      onClick: onUpStreamRuleIndicatorClick
    },
    children: [
      IconNode({
        key: `${key}-SwimlaneUpStreamRuleIndicatorIcon`,
        icon: "ArrowUpLight",
        size: 15,
        color: palette.RULE_INDICATOR_UPSTREAM_ICON_COLOR,
        children: []
      }),
      TextNode({
        key: `${key}-SwimlaneUpStreamRuleRuleIndicatorText`,
        text: upStreamRulesCount.toString(),
        textStyle: style.ruleIndicatorTitle,
        children: []
      })
    ]
  });

  const downstreamIndicator = BoxNode({
    key: `${key}-SwimlaneDownStreamRuleIndicatorContainer`,
    style: style.ruleIndicatorContainer,
    eventHandlers: {
      onClick: onDownStreamRuleIndicatorClick
    },
    children: [
      IconNode({
        key: `${key}-SwimlaneDownStreamRuleIndicatorIcon`,
        icon: "ArrowDownLight",
        size: 15,
        color: palette.RULE_INDICATOR_DOWNSTREAM_ICON_COLOR,
        children: []
      }),
      TextNode({
        key: `${key}-SwimlaneDownStreamRuleIndicatorText`,
        text: downStreamRulesCount.toString(),
        textStyle: style.ruleIndicatorTitle,
        children: []
      })
    ]
  });

  const CollapseTriggerNode = BoxNode({
    key: `${key}-CollapseTrigger`,
    style: style.collapseTrigger,
    eventHandlers: {
      onClick: onCollapseToggle
    },
    children: [
      IconNode({
        key: `${key}-CollapseIcon`,
        icon: expanded ? "ChevronDownLight" : "ChevronRightLight",
        size: 14,
        color: palette.SWIMLANE_CARET,
        style: noStyle,
        children: []
      })
    ]
  });

  const swimlaneRuleIndicators = [];

  if (upStreamRulesCount > 0) {
    swimlaneRuleIndicators.push(upstreamIndicator);
  }
  if (downStreamRulesCount > 0) {
    swimlaneRuleIndicators.push(downstreamIndicator);
  }

  const swimlaneTextStyles = {
    ...style.titleNormalText,
    ...getSwimlaneMaxLength(swimlaneRuleIndicators.length)
  };

  const headerComponent = BoxNode({
    key: `${key}-Header`,
    style: isFlowView ? style.flowHeader : style.header,
    children: [
      BoxNode({
        key: `${key}-HeaderLeft`,
        style: style.headerLeft,
        children: [
          IconNode({
            key: `${key}-Icon`,
            icon: "Kanban",
            size: 20,
            color: palette.SWIMLANE_ICON,
            style: noStyle,
            children: []
          }),
          TextNode({
            text: title || "Untitled swimlane",
            key: `${key}-Title`,
            textStyle: title ? swimlaneTextStyles : style.titleItalicText,
            style: style.title,
            children: []
          })
        ]
      }),
      BoxNode({
        style: styles.dark.ruleIndicatorsWrapper,
        key: `${key}-RuleIndicatorsWrapper`,
        children: swimlaneRuleIndicators
      })
    ],
    ...nonReadonlyPropsHeader,
    eventHandlers: {
      ...nonReadonlyPropsHeader.eventHandlers,
      onClick
    }
  });

  if (!disableExpandAndCollapse) {
    headerComponent.children.push(CollapseTriggerNode);
  }

  if (dropIndicatorIndex > -1) {
    children.splice(dropIndicatorIndex, 0, HorizontalDropIndicator());
  }

  let swimlaneBodyStyle = isFlowView ? style.bodyFlow : style.body;
  if (!expanded) {
    swimlaneBodyStyle = style.bodyHidden;
  } else if (readonly && !isFlowView) {
    // as of now, not using readonly style for flow view
    swimlaneBodyStyle = style.bodyReadonly;
  }
  const bodyComponent = BoxNode({
    key: `${key}-Body`,
    style: swimlaneBodyStyle,
    children
  });

  const childNodes = [headerComponent, bodyComponent];

  if (addChildNode && expanded) {
    childNodes.push(addChildNode);
  }

  return BoxNode({
    key,
    style: {
      ...(selected
        ? isFlowView
          ? style.flowContainerSelected
          : style.containerSelected
        : isFlowView
          ? style.flowContainer
          : style.container),
      ...styleOverride,
      ...(isDroppingBefore
        ? {
            marginLeft:
              (style.container.marginLeft ?? 0) - VERTICAL_DROP_INDICATOR_WIDTH
          }
        : {})
    },
    children: childNodes,
    ...nonReadonlyPropsContainer
  });
};

const noStyle = {};
